import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { sectionTitleLogoStringLight } from "@store/Components/Icons/SectionTitleLogo";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    sectionTitleValue = "sectionTitleValue",
    hideMacpekLogo = "hideMacpekLogo",
}

interface OwnProps extends WidgetProps {
    extendedStyles?: ProductDetailsSectionTitleStyles;
    fields: {
        [fields.sectionTitleValue]: string;
        [fields.hideMacpekLogo]: boolean;
    };
}

export interface ProductDetailsSectionTitleStyles {
    container?: InjectableCss;
    horizontalLineItem?: InjectableCss;
    horizontalLine?: InjectableCss;
    titleWrapperItem?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    backgroundLogo?: InjectableCss;
}

export const productDetailsSectionTitleStyles: ProductDetailsSectionTitleStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 140px;
            width: 100%;
        `,
    },
    horizontalLineItem: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            flex-grow: 5;
        `,
    },
    horizontalLine: {
        css: css`
            width: 100%;
            height: 2px;
            background-color: ${getColor("common.border")};
        `,
    },
    titleWrapperItem: {
        css: css`
            position: relative;
            flex-grow: 1;
            text-align: center;
            padding: 0 15px;
        `,
    },
    sectionTitle: {
        css: css`
            position: relative;
            z-index: 1;
            margin: 0;
        `,
    },
    backgroundLogo: {
        css: css`
            position: absolute;
            top: 20%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-image: url(${`data:image/svg+xml;base64,${sectionTitleLogoStringLight}`});
            background-position: center;
            background-size: contain;
            background-repeat: no-repeat;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["130px", null, "188px", null, null], properties: ["width"] },
                        { values: ["65px", null, "84px", null, null], properties: ["height"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
};

const ProductDetailsSectionTitle: FC<OwnProps> = ({ id, extendedStyles, fields }) => {
    const [styles] = React.useState(() => mergeToNew(productDetailsSectionTitleStyles, extendedStyles));
    const { sectionTitleValue, hideMacpekLogo } = fields;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.horizontalLineItem}>
                <StyledWrapper {...styles.horizontalLine}></StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.titleWrapperItem}>
                <Typography variant="h2" {...styles.sectionTitle}>
                    {sectionTitleValue}
                </Typography>
                {!hideMacpekLogo && <StyledWrapper {...styles.backgroundLogo}></StyledWrapper>}
            </StyledWrapper>
            <StyledWrapper {...styles.horizontalLineItem}>
                <StyledWrapper {...styles.horizontalLine}></StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const storeProductDetailsSectionTitle: WidgetModule = {
    component: connect()(ProductDetailsSectionTitle),
    definition: {
        displayName: "Store Section Title",
        group: "Product Details",
        fieldDefinitions: [
            {
                name: fields.sectionTitleValue,
                displayName: "Section Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.hideMacpekLogo,
                displayName: "Show Macpek's Logo",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default storeProductDetailsSectionTitle;
